/**
 * shared/attachmentsPanel/useAttachmentUploads.js - picking, auto-labelling,
 * dropping and uploading files (one, many, or staged before the record
 * exists), the drop notice, and delete.
 * Moved verbatim out of AttachmentsPanel.jsx; the panel passes in the
 * values it reads.
 */
import { useCallback, useState, useRef, useEffect } from 'react';
import { REF_OPTIONAL } from '../../../utils/attachmentPreview';
import { suggestLabel, confirmedLabel } from '../../../utils/docSuggest';
import { notifyDocsChanged } from '../../../hooks/useDocIndex';
import { UNFILED } from '../../../data/docFolders';
import { snapshotTransfer, resolveSnapshot } from '../../../utils/droppedFiles';

export function useAttachmentUploads({
  setPicked,
  setError,
  suggest,
  setSuggested,
  setDocType,
  setDocRef,
  inputRef,
  docType,
  picked,
  docRef,
  pending,
  onStage,
  staged,
  base,
  projectId,
  load,
  onChanged,
  view,
  uploading,
  clearPicker,
  setUploading,
}) {
  // Auto-label from the file name (GAT-B-5306 -> Quote 5306; numbers matched
  // against the project's own PO/invoice/quote numbers). Prefilled, never
  // forced — the user can change both fields before uploading.
  const pickFile = useCallback(
    (file) => {
      setPicked(file);
      setError('');
      const s = file ? suggestLabel(file.name, suggest) : null;
      setSuggested(s);
      setDocType(s ? s.type : '');
      setDocRef(s ? s.ref : '');
      // A DROPPED file never touches the <input>, so the browser's own
      // "No file chosen" text stayed there while the label panel was open and
      // nothing on screen said which file you had just dropped. Mirror it into
      // the input so the two can never disagree. No-op when Browse put it there.
      try {
        if (inputRef.current) {
          const dt = new DataTransfer();
          if (file) dt.items.add(file);
          inputRef.current.files = dt.files;
        }
      } catch {
        /* no DataTransfer: the panel below still names the file */
      }
    },
    [suggest, inputRef, setDocRef, setDocType, setError, setPicked, setSuggested]
  );

  const needsRef = docType && !REF_OPTIONAL.has(docType);
  const ready = picked && docType && (!needsRef || docRef.trim());

  /**
   * Drag-and-drop. Dropping ONE file just fills the picker, so it goes through
   * the same label-then-confirm step as the Browse button. Dropping SEVERAL
   * uploads them straight away - stopping to label a dozen customer CAD files
   * one at a time is the reason people give up and email them instead.
   *
   * A bulk drop is labeled by the same filename matcher the picker prefills
   * with (utils/docSuggest), which only fires when the number matches one of
   * THIS project's own quote/PO/invoice numbers. No confident match means
   * General - a wrong label would break a clickable number on the Projects tab,
   * so the guess has to be the safe one.
   */
  const [dropTarget, setDropTarget] = useState(null); // folder key being hovered
  const [bulk, setBulk] = useState(null); // { done, total, note }

  const uploadMany = useCallback(
    async (files, folderKey, subId) => {
      const list = Array.from(files || []).filter(Boolean);
      if (!list.length) return;
      if (pending) {
        onStage([
          ...(staged || []),
          ...list.map((file) => {
            const sg = confirmedLabel(file.name, suggest);
            return {
              file,
              doc_type: sg ? sg.type : 'general',
              doc_ref: sg ? sg.ref : '',
              folder: folderKey && folderKey !== UNFILED.key ? folderKey : null,
            };
          }),
        ]);
        return;
      }
      setBulk({ done: 0, total: list.length, note: '' });
      let labeled = 0;
      let failed = 0;
      for (let i = 0; i < list.length; i++) {
        const f = list[i];
        const s = confirmedLabel(f.name, suggest);
        const fd = new FormData();
        fd.append('file', f);
        fd.append('doc_type', s ? s.type : 'general');
        fd.append('doc_ref', s ? s.ref : '');
        if (folderKey && folderKey !== UNFILED.key) {
          fd.append('folder', folderKey);
          if (subId) fd.append('subfolder_id', subId);
        }
        try {
          const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/attachments`, { method: 'POST', body: fd });
          if (r.ok) {
            if (s) labeled++;
          } else {
            failed++;
          }
        } catch {
          failed++;
        }
        setBulk({ done: i + 1, total: list.length, note: '' });
      }
      await load();
      notifyDocsChanged();
      if (onChanged) onChanged();
      setBulk({
        done: list.length,
        total: list.length,
        note: [
          `${list.length - failed} file${list.length - failed === 1 ? '' : 's'} added`,
          labeled ? `${labeled} auto-labeled` : '',
          failed ? `${failed} failed` : '',
        ]
          .filter(Boolean)
          .join(' · '),
      });
      setTimeout(() => setBulk(null), 4000);
    },
    [projectId, suggest, load, onChanged, pending, staged, onStage, base]
  );

  /**
   * What a drop/paste could not bring in by itself: a Teams / new Outlook /
   * SharePoint file only hands the browser a LINK, and that link needs the
   * person's Microsoft sign-in (utils/droppedFiles). Say so, with the quick
   * way in, instead of doing nothing. { kind: 'links', links } | { kind: 'none' }
   * | { kind: 'busy' }
   */
  const [notice, setNotice] = useState(null);

  /** Files in hand -> the same path an Explorer drop always took: ONE file
   *  into the open folder fills the picker, anything else uploads at once. */
  const takeFiles = useCallback(
    (list, folderKey, subId) => {
      if (!list.length) return;
      if (list.length === 1 && folderKey === (view && view.key)) {
        pickFile(list[0]);
        return;
      }
      uploadMany(list, folderKey, subId);
    },
    [uploadMany, pickFile, view]
  );

  /** A drop or a paste. The DataTransfer is only readable during the event,
   *  so it is snapshotted first; links are fetched afterwards. */
  const takeTransfer = useCallback(
    async (dt, folderKey, subId, opts) => {
      const snap = snapshotTransfer(dt, opts);
      if (snap.files.length) {
        setNotice(null);
        takeFiles(snap.files, folderKey, subId);
        return;
      }
      if (!snap.urls.length) {
        if (snap.hadData && !(opts && opts.quietIfEmpty)) setNotice({ kind: 'none' });
        return;
      }
      setNotice({ kind: 'busy' });
      const { files: got, links } = await resolveSnapshot(snap);
      setNotice(links.length ? { kind: 'links', links } : null);
      takeFiles(got, folderKey, subId);
    },
    [takeFiles]
  );

  /** Shared by the in-folder drop zone and the folder cards on the grid. */
  const handleDrop = useCallback(
    (e, folderKey, subId) => {
      e.preventDefault();
      setDropTarget(null);
      takeTransfer(e.dataTransfer, folderKey, subId);
    },
    [takeTransfer]
  );

  /* Ctrl+V while the Doc Hub is on screen: files copied in Explorer, a
     screenshot, an image copied from Teams/Outlook. Lands in the open folder
     (Unfiled from the folder grid). Never while typing in a field - that
     paste belongs to the field - and never one another handler already took. */
  const rootRef = useRef(null);
  useEffect(() => {
    const onPaste = (e) => {
      if (e.defaultPrevented || !rootRef.current || !rootRef.current.isConnected) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      // Another window opened on top (Report a problem ...) owns the paste.
      const dialogs = document.querySelectorAll('[role="dialog"]');
      const top = dialogs.length ? dialogs[dialogs.length - 1] : null;
      if (top && !top.contains(rootRef.current)) return;
      const cd = e.clipboardData;
      if (!cd) return;
      const hasFile = Array.from(cd.items || []).some((it) => it && it.kind === 'file') || (cd.files && cd.files.length > 0);
      const text = typeof cd.getData === 'function' ? cd.getData('text/plain') || '' : '';
      const hasUrl = /^\s*(https?:|data:)\S+\s*$/i.test(text);
      if (!hasFile && !hasUrl) return;
      e.preventDefault();
      takeTransfer(cd, view ? view.key : UNFILED.key, view ? view.subId : null, { renameGeneric: true, quietIfEmpty: true });
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [takeTransfer, view]);

  const upload = useCallback(async () => {
    if (!ready || uploading) return;
    // No record yet: hold it and let the modal upload after the save.
    if (pending) {
      onStage([
        ...(staged || []),
        {
          file: picked,
          doc_type: docType,
          doc_ref: docRef.trim(),
          folder: view && view.key !== UNFILED.key ? view.key : null,
        },
      ]);
      clearPicker();
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', picked);
      fd.append('doc_type', docType);
      fd.append('doc_ref', docRef.trim());
      // Lands in whatever folder you are standing in. Unfiled stores nothing.
      if (view && view.key !== UNFILED.key) {
        fd.append('folder', view.key);
        if (view.subId) fd.append('subfolder_id', view.subId);
      }
      // No Content-Type header — the browser sets the multipart boundary.
      const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/attachments`, {
        method: 'POST',
        body: fd,
      });
      if (r.ok) {
        clearPicker();
        await load();
        notifyDocsChanged(); // a quote/PO number becomes a link right away
        if (onChanged) onChanged();
      } else {
        const j = await r.json().catch(() => null);
        setError(j?.error || 'Upload failed.');
      }
    } catch {
      setError('Upload failed - check your connection.');
    } finally {
      setUploading(false);
    }
  }, [
    ready,
    uploading,
    picked,
    docType,
    docRef,
    projectId,
    view,
    load,
    clearPicker,
    onChanged,
    pending,
    staged,
    onStage,
    base,
    setError,
    setUploading,
  ]);

  const remove = useCallback(
    async (attId, filename, stagedIndex) => {
      if (stagedIndex != null) {
        onStage((staged || []).filter((_, i) => i !== stagedIndex));
        return;
      }
      // Deleting takes the row AND the file off disk - there is no undo, and
      // anyone who can upload can do it, so the dialog has to be the brake.
      if (
        !window.confirm(
          `Delete "${filename || 'this file'}"?

` +
            'This permanently removes the file for everyone. It cannot be undone ' +
            'and there is no recycle bin - the file would have to be re-uploaded.'
        )
      )
        return;
      try {
        const r = await fetch(`${base}/attachments/${encodeURIComponent(attId)}`, { method: 'DELETE' });
        if (r.ok) {
          await load();
          notifyDocsChanged();
          if (onChanged) onChanged();
        } else if (r.status === 403) alert('Only operations or admin can delete attachments.');
      } catch {
        /* noop */
      }
    },
    [load, onChanged, staged, onStage, base]
  );
  return { rootRef, bulk, notice, setNotice, handleDrop, setDropTarget, dropTarget, pickFile, upload, ready, remove };
}
